/* KDK-PERF 1.0.0 — local, dependency-free scheduling. No tracking or app disabling. */
(function () {
  'use strict';
  if (window.KDKPerformance) return;
  var hasIO = 'IntersectionObserver' in window;
  var counters = {legacyLoaded: 0, sectionsStarted: 0, videosPrepared: 0};

  // A sibling anchor remains measurable even when Shopify hides an empty section.
  function nearSection(element, callback, options) {
    options = options || {};
    var observer = null, anchor = null, cancelled = false, started = false;
    function cleanup() {
      if (observer) observer.disconnect();
      if (anchor && anchor.parentNode) anchor.parentNode.removeChild(anchor);
    }
    function start() {
      if (cancelled || started || !element.isConnected) return;
      started = true;
      cleanup();
      counters.sectionsStarted++;
      callback();
    }
    if (!hasIO || (window.Shopify && window.Shopify.designMode)) {
      start();
    } else {
      var wrapper = element.closest('.shopify-section') || element;
      anchor = document.createElement('span');
      anchor.setAttribute('data-kdk-perf-anchor', element.getAttribute('data-section-id') || 'section');
      anchor.setAttribute('aria-hidden', 'true');
      anchor.style.cssText = 'display:block;height:1px;width:1px;margin-bottom:-1px;pointer-events:none;overflow:hidden;';
      wrapper.parentNode.insertBefore(anchor, wrapper);
      observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) start();
      }, {rootMargin: options.rootMargin || '800px 0px', threshold: 0});
      observer.observe(anchor);
    }
    return function () { cancelled = true; cleanup(); };
  }

  // Swift/legacy compatibility: only the exact lazy-* consumers are observed.
  // Lazysizes' data-src/Image--lazyLoad mechanism is deliberately NOT touched.
  var legacySelector = '[lazy-src],[lazy-href],[lazy-bg]';
  var legacyObserver = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        legacyObserver.unobserve(entry.target);
        loadLegacy(entry.target);
      }
    });
  }, {rootMargin: '400px 0px', threshold: 0}) : null;

  function loadLegacy(element) {
    if (!element || !element.isConnected) return;
    var src = element.getAttribute('lazy-src');
    var href = element.getAttribute('lazy-href');
    var bg = element.getAttribute('lazy-bg');
    if (src) {
      if (element.getAttribute('src') !== src) element.setAttribute('src', src);
      element.removeAttribute('lazy-src');
      counters.legacyLoaded++;
    }
    if (href) {
      if (element.getAttribute('href') !== href) element.setAttribute('href', href);
      element.removeAttribute('lazy-href');
      counters.legacyLoaded++;
    }
    if (bg) {
      element.style.backgroundImage = 'url(' + JSON.stringify(bg) + ')';
      element.style.backgroundPosition = 'center';
      element.style.backgroundSize = 'cover';
      element.removeAttribute('lazy-bg');
      counters.legacyLoaded++;
    }
  }
  function registerLegacy(element) {
    if (!element.matches(legacySelector)) return;
    // Styles and scripts must not depend on an invisible element intersecting.
    if (!legacyObserver || /^(LINK|SCRIPT|SOURCE)$/.test(element.tagName)) loadLegacy(element);
    else legacyObserver.observe(element);
  }
  function scanLegacy(root) {
    if (!root || root.nodeType !== 1) return;
    registerLegacy(root);
    root.querySelectorAll(legacySelector).forEach(registerLegacy);
  }
  function forgetLegacy(root) {
    if (!legacyObserver || !root || root.nodeType !== 1) return;
    legacyObserver.unobserve(root);
    root.querySelectorAll(legacySelector).forEach(function (el) { legacyObserver.unobserve(el); });
  }

  // Lower-page framing videos: prepare nearby, play only on screen, pause on hide.
  var videos = new Set();
  var prepared = new WeakSet();
  var visibleVideos = new WeakSet();
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function prepareVideo(video) {
    if (prepared.has(video)) return;
    var sources = video.querySelectorAll('source[data-kdk-video-src]');
    sources.forEach(function (source) {
      source.src = source.getAttribute('data-kdk-video-src');
      source.removeAttribute('data-kdk-video-src');
    });
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    prepared.add(video);
    video.load();
    counters.videosPrepared++;
  }
  function playVideo(video) {
    if (document.hidden || reducedMotion.matches || !visibleVideos.has(video)) return;
    prepareVideo(video);
    var promise = video.play();
    if (promise && promise.catch) promise.catch(function () {
      // Low-power Safari / autoplay denial: keep poster and offer native controls.
      if (video.isConnected && video.paused) video.controls = true;
    });
  }
  var videoPrepareObserver = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        if (!reducedMotion.matches) prepareVideo(entry.target);
        videoPrepareObserver.unobserve(entry.target);
      }
    });
  }, {rootMargin: '600px 0px', threshold: 0}) : null;
  var videoPlayObserver = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        visibleVideos.add(entry.target);
        playVideo(entry.target);
      } else {
        visibleVideos.delete(entry.target);
        entry.target.pause();
      }
    });
  }, {threshold: 0.01}) : null;
  function registerVideo(video) {
    if (videos.has(video)) return;
    videos.add(video);
    if (reducedMotion.matches) video.controls = true;
    // Native controls remain usable with reduced motion (do not auto-play).
    video.addEventListener('pointerdown', function () { prepareVideo(video); }, {passive: true});
    video.addEventListener('keydown', function () { prepareVideo(video); });
    video.addEventListener('play', function () { prepareVideo(video); });
    if (hasIO) {
      videoPrepareObserver.observe(video);
      videoPlayObserver.observe(video);
    } else {
      visibleVideos.add(video);
      if (!reducedMotion.matches) playVideo(video);
    }
  }
  function scanVideos(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches('[data-kdk-deferred-video]')) registerVideo(root);
    root.querySelectorAll('[data-kdk-deferred-video]').forEach(registerVideo);
  }
  function forgetVideos(root) {
    videos.forEach(function (video) {
      if (root === video || (root.contains && root.contains(video))) {
        video.pause();
        if (videoPrepareObserver) videoPrepareObserver.unobserve(video);
        if (videoPlayObserver) videoPlayObserver.unobserve(video);
        visibleVideos.delete(video);
        videos.delete(video);
      }
    });
  }
  document.addEventListener('visibilitychange', function () {
    videos.forEach(function (video) {
      if (document.hidden) video.pause();
      else playVideo(video);
    });
  });
  function motionChanged() {
    videos.forEach(function (video) {
      if (reducedMotion.matches) { video.pause(); video.controls = true; }
      else playVideo(video);
    });
  }
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', motionChanged);
  else if (reducedMotion.addListener) reducedMotion.addListener(motionChanged);

  function init() {
    scanLegacy(document.documentElement);
    scanVideos(document.documentElement);
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'attributes') { registerLegacy(record.target); return; }
        record.addedNodes.forEach(scanLegacy);
        record.removedNodes.forEach(function (root) { forgetLegacy(root); });
      });
    }).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['lazy-src', 'lazy-href', 'lazy-bg']
    });
    document.addEventListener('shopify:section:load', function (event) { scanVideos(event.target); });
    document.addEventListener('shopify:section:unload', function (event) { forgetVideos(event.target); });
  }
  window.KDKPerformance = {
    version: '1.0.0', nearSection: nearSection,
    status: function () { return Object.assign({version: '1.0.0'}, counters); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
